import { onCLS, onFCP, onLCP, onTTFB, onINP, Metric } from 'web-vitals';
import { monitoring } from './monitoring';
import { logger } from './logger';

/**
 * Web Vitals thresholds for scoring
 */
const THRESHOLDS = {
  LCP: { good: 2500, poor: 4000 }, // Largest Contentful Paint
  INP: { good: 200, poor: 500 }, // Interaction to Next Paint (replaced FID)
  CLS: { good: 0.1, poor: 0.25 }, // Cumulative Layout Shift
  FCP: { good: 1800, poor: 3000 }, // First Contentful Paint
  TTFB: { good: 800, poor: 1800 }, // Time to First Byte
};

/**
 * Get rating for a metric value
 */
function getRating(name: string, value: number): 'good' | 'needs-improvement' | 'poor' {
  const threshold = THRESHOLDS[name as keyof typeof THRESHOLDS];
  if (!threshold) return 'needs-improvement';

  if (value <= threshold.good) return 'good';
  if (value >= threshold.poor) return 'poor';
  return 'needs-improvement';
}

/**
 * Send metric to monitoring service
 */
function sendToMonitoring(metric: Metric) {
  const rating = getRating(metric.name, metric.value);

  // Track in monitoring service
  monitoring.trackEvent(`web_vital_${metric.name.toLowerCase()}`, {
    value: metric.value,
    rating,
    navigationType: metric.navigationType,
    delta: metric.delta,
    id: metric.id,
    entries: metric.entries?.length || 0,
  });

  // Log for debugging
  logger.debug(`Web Vital: ${metric.name}`, {
    value: metric.value,
    rating,
  });

  // Alert on poor performance
  if (rating === 'poor') {
    logger.warn(`Poor ${metric.name} performance detected`, {
      value: metric.value,
      threshold: THRESHOLDS[metric.name as keyof typeof THRESHOLDS]?.poor,
    });
  }
}

/**
 * Initialize Web Vitals monitoring
 */
export function initWebVitals() {
  // Core Web Vitals
  onLCP(sendToMonitoring); // Largest Contentful Paint
  onINP(sendToMonitoring); // Interaction to Next Paint (replaced FID)
  onCLS(sendToMonitoring); // Cumulative Layout Shift

  // Additional metrics
  onFCP(sendToMonitoring); // First Contentful Paint
  onTTFB(sendToMonitoring); // Time to First Byte

  logger.info('Web Vitals monitoring initialized');
}

/**
 * Get current Web Vitals status
 */
export function getWebVitalsStatus(
  name?: string,
  value?: number,
):
  | {
      supported: boolean;
      metrics: string[];
    }
  | 'good'
  | 'needs-improvement'
  | 'poor' {
  // If called with name and value, return the rating
  if (name && value !== undefined) {
    return getRating(name, value);
  }

  // Otherwise return status object
  return {
    supported: typeof window !== 'undefined' && 'PerformanceObserver' in window,
    metrics: ['LCP', 'INP', 'CLS', 'FCP', 'TTFB'],
  };
}

/**
 * Report custom performance mark
 */
export function markPerformance(markName: string) {
  if (typeof window !== 'undefined' && 'performance' in window) {
    performance.mark(markName);

    // Track custom mark
    monitoring.trackEvent('performance_mark', {
      mark: markName,
      timestamp: performance.now(),
    });
  }
}

/**
 * Measure performance between two marks
 */
export function measurePerformance(measureName: string, startMark: string, endMark: string) {
  if (typeof window !== 'undefined' && 'performance' in window) {
    try {
      performance.measure(measureName, startMark, endMark);

      const measures = performance.getEntriesByName(measureName, 'measure');
      const measure = measures[measures.length - 1];

      if (measure) {
        monitoring.trackEvent('performance_measure', {
          name: measureName,
          duration: measure.duration,
          startTime: measure.startTime,
        });

        logger.debug(`Performance measure: ${measureName}`, {
          duration: `${measure.duration.toFixed(2)}ms`,
        });
      }
    } catch (error) {
      logger.error('Failed to measure performance', error);
    }
  }
}

/**
 * Track component render performance
 */
export function trackComponentPerformance(componentName: string, renderTime: number) {
  monitoring.trackEvent('component_render', {
    component: componentName,
    renderTime,
    slow: renderTime > 16, // More than one frame (60fps)
  });

  if (renderTime > 100) {
    logger.warn(`Slow component render detected: ${componentName}`, {
      renderTime: `${renderTime.toFixed(2)}ms`,
    });
  }
}

/**
 * Track route change performance
 */
export function trackRouteChange(from: string, to: string, duration: number) {
  monitoring.trackEvent('route_change', {
    from,
    to,
    duration,
    slow: duration > 1000, // More than 1 second
  });

  if (duration > 2000) {
    logger.warn(`Slow route change detected: ${from} -> ${to}`, {
      duration: `${duration.toFixed(2)}ms`,
    });
  }
}
